import { useEffect, useId, useRef, useState } from 'react';
import { createSuggester, suggestKey, type Suggestion } from './addressSuggest';

/** Address search with 2GIS suggestions. Hidden without a key; says so when the service fails. */
export default function AddressSearch({ onPick, unavailableText = 'Поиск недоступен. Выберите точку на карте или введите координаты.' }: {
  onPick: (item: Suggestion) => void;
  /** Where the search is offered without a map and coordinates beside it, the words say what to do instead. */
  unavailableText?: string;
}) {
  const id = useId();
  const key = suggestKey().trim();
  const [text, setText] = useState('');
  const [items, setItems] = useState<Suggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [pending, setPending] = useState(false);
  const [searched, setSearched] = useState('');
  const [unavailable, setUnavailable] = useState(false);
  const suggester = useRef<ReturnType<typeof createSuggester> | null>(null);
  useEffect(() => {
    if (!key) return;
    const s = createSuggester({ key, onPending: setPending, onUnavailable: () => { setUnavailable(true); setItems([]); setOpen(false); },
      onResult: (query, list) => { setItems(list); setSearched(query); setActive(-1); setOpen(list.length > 0 || query.length >= 3); } });
    suggester.current = s;
    return () => { s.dispose(); suggester.current = null; };
  }, [key]);
  if (!key) return null;
  if (unavailable) return <p className="dev-muted" role="status">{unavailableText}</p>;
  function choose(item: Suggestion): void {
    setText(item.title); setOpen(false); setItems([]); setActive(-1); suggester.current?.input('');
    onPick(item);
  }
  const listId = `${id}-list`;
  const noResults = open && !pending && items.length === 0 && searched.length >= 3;
  return <div className="location-search">
    <label htmlFor={`${id}-input`}>Найти адрес или место</label>
    <input id={`${id}-input`} type="text" role="combobox" autoComplete="off" aria-autocomplete="list" aria-expanded={open && items.length > 0}
      aria-controls={listId} aria-activedescendant={active >= 0 ? `${id}-opt-${active}` : undefined} value={text}
      placeholder="Например, Московский вокзал"
      onChange={(e) => { setText(e.target.value); suggester.current?.input(e.target.value); }}
      onKeyDown={(e) => {
        if (e.key === 'ArrowDown' && items.length) { e.preventDefault(); setOpen(true); setActive((i) => (i + 1) % items.length); }
        else if (e.key === 'ArrowUp' && items.length) { e.preventDefault(); setOpen(true); setActive((i) => (i <= 0 ? items.length - 1 : i - 1)); }
        else if (e.key === 'Enter' && open && active >= 0 && items[active]) { e.preventDefault(); choose(items[active]); }
        else if (e.key === 'Escape' && open) { e.preventDefault(); setOpen(false); setActive(-1); }
      }} />
    <ul id={listId} role="listbox" aria-label="Подсказки адреса" hidden={!(open && items.length > 0)}>
      {items.map((item, i) => <li key={item.id} id={`${id}-opt-${i}`} role="option" aria-selected={i === active}
        className={i === active ? 'is-active' : undefined}
        onMouseDown={(e) => { e.preventDefault(); choose(item); }}>
        {item.title}{item.subtitle ? <span className="dev-muted"> · {item.subtitle}</span> : null}
      </li>)}
    </ul>
    <p className="dev-muted" role="status">{pending ? 'Ищу…' : noResults ? 'Ничего не найдено.' : ''}</p>
  </div>;
}
